import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
    ArrowLeft, Pencil, User, Phone, Mail, Euro, Plus, Download, Trash2,
    ChevronDown, ChevronRight, Save, RefreshCw, TrendingDown, TrendingUp, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const FR_MONTHS = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

export default function FicheDemenageur() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [fiche, setFiche] = useState(null);
    const [loading, setLoading] = useState(true);

    // Protection : non-admin → redirect
    useEffect(() => {
        if (user && !isAdmin) {
            toast.error('Accès réservé aux administrateurs');
            navigate('/espace-demenageurs');
        }
    }, [user, isAdmin, navigate]);

    const fetchFiche = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API_URL}/api/demenageurs-staff/${id}/fiche`, { withCredentials: true });
            setFiche(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchFiche(); }, [id]); // eslint-disable-line

    // --- Edit infos ---
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({ nom: '', prenom: '', telephone: '', email: '', tarif_journee: '' });
    const openEdit = () => {
        if (!fiche) return;
        setEditForm({
            nom: fiche.nom || '', prenom: fiche.prenom || '',
            telephone: fiche.telephone || '', email: fiche.email || '',
            tarif_journee: fiche.tarif_journee || '',
        });
        setEditOpen(true);
    };
    const saveEdit = async () => {
        try {
            await axios.put(`${API_URL}/api/demenageurs-staff/${id}`, {
                nom: editForm.nom, prenom: editForm.prenom,
                telephone: editForm.telephone, email: editForm.email,
                tarif_journee: Number(editForm.tarif_journee || 0),
            }, { withCredentials: true });
            toast.success('Informations mises à jour');
            setEditOpen(false);
            fetchFiche();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    // --- Création mensualité ---
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({ mois: '' });
    const openCreate = () => {
        const now = new Date();
        const mois = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        setCreateForm({ mois });
        setCreateOpen(true);
    };
    const saveCreate = async () => {
        if (!createForm.mois) { toast.error('Mois obligatoire'); return; }
        try {
            await axios.post(`${API_URL}/api/pointage-mensualites`, {
                staff_id: id,
                mois: createForm.mois,
            }, { withCredentials: true });
            toast.success('Calcul mensuel créé');
            setCreateOpen(false);
            fetchFiche();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    // --- Ajout ligne dépense / paiement ---
    const [lineDialog, setLineDialog] = useState({ open: false, kind: null, mid: null });
    const [lineForm, setLineForm] = useState({ montant: '', date: '', remarque: '' });

    const openLineDialog = (mid, kind) => {
        const today = new Date().toISOString().slice(0, 10);
        setLineDialog({ open: true, kind, mid });
        setLineForm({ montant: '', date: today, remarque: '' });
    };

    const saveLine = async () => {
        const m = Number(lineForm.montant || 0);
        if (!m || m <= 0) { toast.error('Montant obligatoire'); return; }
        try {
            await axios.post(
                `${API_URL}/api/pointage-mensualites/${lineDialog.mid}/${lineDialog.kind}`,
                { montant: m, date: lineForm.date, remarque: lineForm.remarque },
                { withCredentials: true },
            );
            toast.success(lineDialog.kind === 'depenses' ? 'Dépense ajoutée' : 'Paiement ajouté');
            setLineDialog({ open: false, kind: null, mid: null });
            fetchFiche();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    const deleteLine = async (mid, kind, lineId) => {
        if (!window.confirm('Supprimer cette ligne ?')) return;
        try {
            await axios.delete(
                `${API_URL}/api/pointage-mensualites/${mid}/${kind}/${lineId}`,
                { withCredentials: true },
            );
            toast.success('Ligne supprimée');
            fetchFiche();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    const deleteMensualite = async (m) => {
        if (!window.confirm(`Supprimer le calcul "${m.mois_label}" ?`)) return;
        try {
            await axios.delete(`${API_URL}/api/pointage-mensualites/${m.id}`, { withCredentials: true });
            toast.success('Supprimé');
            fetchFiche();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Erreur');
        }
    };

    // --- Expand mensualité ---
    const [expanded, setExpanded] = useState({});
    const toggle = (mid) => setExpanded(p => ({ ...p, [mid]: !p[mid] }));

    if (loading || !fiche) {
        return (
            <Layout>
                <div className="flex items-center justify-center py-20 text-gray-400">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                </div>
            </Layout>
        );
    }

    const { soldes, mensualites } = fiche;
    const sCur = soldes.mois_en_cours;
    const sPrev = soldes.mois_precedent;

    // Options de mois (année en cours - 1 → année en cours)
    const monthOptions = [];
    const now = new Date();
    for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
        for (let m = 12; m >= 1; m--) {
            monthOptions.push({ value: `${y}-${String(m).padStart(2, '0')}`, label: `${FR_MONTHS[m - 1]} ${y}` });
        }
    }

    return (
        <Layout>
            <div className="space-y-6" data-testid="fiche-demenageur">
                {/* Header */}
                <div className="flex items-start gap-4">
                    <button
                        onClick={() => {
                            if (window.history.length > 1) navigate(-1);
                            else navigate('/espace-demenageurs?tab=pointage&sub=demenageurs');
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        data-testid="fiche-back"
                    >
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-[#1B365D]">Fiche déménageur</h1>
                        <p className="text-sm text-gray-500">Informations et historique des prestations.</p>
                    </div>
                </div>

                {/* Section Informations */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="fiche-infos">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Informations</h2>
                        <button onClick={openEdit} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 transition-colors" data-testid="fiche-edit">
                            <Pencil className="w-3.5 h-3.5" /> Modifier
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <InfoCard icon={User} color="bg-purple-50 text-purple-600" label="Nom" value={`${fiche.prenom || ''} ${fiche.nom || ''}`.trim() || '—'} />
                        <InfoCard icon={Phone} color="bg-emerald-50 text-emerald-600" label="Téléphone" value={fiche.telephone || '—'} />
                        <InfoCard icon={Mail} color="bg-amber-50 text-amber-600" label="E-mail" value={fiche.email || '—'} />
                        <InfoCard icon={Euro} color="bg-purple-50 text-purple-600" label="Tarif / jour" value={fmtEur(fiche.tarif_journee)} />
                    </div>
                </div>

                {/* Section Soldes */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="fiche-soldes">
                    <div className="flex items-start justify-between mb-4 flex-wrap gap-2">
                        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Soldes</h2>
                        <span className="text-xs italic text-gray-500">Jours × tarif/jour + paniers + autres</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Mois précédent */}
                        <div className="border border-gray-200 rounded-xl p-4" data-testid="solde-prev">
                            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
                                Mois précédent <span className="text-gray-400">— {sPrev.mois_label}</span>
                            </div>
                            <div className="text-3xl font-bold text-[#1B365D]">{fmtEur(sPrev.base)}</div>
                            <div className="text-[11px] text-gray-500 mt-2 flex gap-3 flex-wrap">
                                <span><b>{sPrev.nb_jours}j</b> × {fmtEur(sPrev.tarif)}</span>
                                <span>+{fmtEur(sPrev.total_paniers)} <span className="text-gray-400">panier{sPrev.nb_paniers > 1 ? 's' : ''}</span></span>
                                <span>+{fmtEur(sPrev.total_autres)} <span className="text-gray-400">autres</span></span>
                            </div>
                        </div>

                        {/* Mois en cours */}
                        <div className="border-2 border-blue-200 bg-blue-50/50 rounded-xl p-4" data-testid="solde-cur">
                            <div className="text-[10px] uppercase tracking-wider text-blue-700 mb-1 font-semibold">
                                Mois en cours <span className="font-normal">— {sCur.mois_label}</span>
                            </div>
                            <div className="text-3xl font-bold text-blue-700">{fmtEur(sCur.base)}</div>
                            <div className="text-[11px] text-gray-600 mt-2 flex gap-3 flex-wrap">
                                <span><b>{sCur.nb_jours}j</b> × {fmtEur(sCur.tarif)}</span>
                                <span>+{fmtEur(sCur.total_paniers)} <span className="text-gray-400">panier{sCur.nb_paniers > 1 ? 's' : ''}</span></span>
                                <span>+{fmtEur(sCur.total_autres)} <span className="text-gray-400">autres</span></span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section Mensualités */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-testid="fiche-mensualites">
                    <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                        <h2 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Calculs mensuels</h2>
                        <button onClick={openCreate} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors" data-testid="mens-create">
                            <Plus className="w-4 h-4" /> Créer un calcul mensuel
                        </button>
                    </div>
                    {mensualites.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-sm">Aucun calcul mensuel. Cliquez sur « Créer un calcul mensuel » pour commencer.</div>
                    ) : (
                        <div className="space-y-2">
                            {mensualites.map(m => {
                                const isOpen = !!expanded[m.id];
                                return (
                                    <div key={m.id} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`mens-row-${m.id}`}>
                                        <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 gap-3">
                                            <button onClick={() => toggle(m.id)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                                                {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                                <div className="h-8 w-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                                                    <Euro className="w-4 h-4" />
                                                </div>
                                                <div className="min-w-0">
                                                    <div className="font-semibold text-[#1B365D]">Calcul <span className="capitalize">{m.mois_label}</span></div>
                                                    <div className="text-[11px] text-gray-500">
                                                        Base {fmtEur(m.base)} · {m.nb_jours}j×{fmtEur(m.tarif)} +{fmtEur(m.total_paniers)} +{fmtEur(m.total_autres)}
                                                    </div>
                                                </div>
                                            </button>
                                            <div className="text-right flex-shrink-0">
                                                <div className="text-[10px] uppercase tracking-wider text-gray-400">Vente finale</div>
                                                <div className="font-bold text-blue-700">{fmtEur(m.vente_finale)}</div>
                                            </div>
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                                <button
                                                    onClick={() => openLineDialog(m.id, 'depenses')}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 transition-colors text-xs font-semibold border border-red-200"
                                                    title="Ajouter une dépense"
                                                    data-testid={`mens-add-depense-${m.id}`}
                                                >
                                                    <TrendingUp className="w-3.5 h-3.5" /> Dépense
                                                </button>
                                                <button
                                                    onClick={() => openLineDialog(m.id, 'paiements')}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors text-xs font-semibold border border-emerald-200"
                                                    title="Ajouter un paiement"
                                                    data-testid={`mens-add-paiement-${m.id}`}
                                                >
                                                    <TrendingDown className="w-3.5 h-3.5" /> Paiement
                                                </button>
                                                <button
                                                    onClick={async () => {
                                                        const safeMois = (m.mois_label || m.mois || 'mois').replace(/\s+/g, '-');
                                                        try {
                                                            const res = await axios.get(
                                                                `${API_URL}/api/pointage-mensualites/${m.id}/pdf`,
                                                                { responseType: 'blob', withCredentials: true },
                                                            );
                                                            const blob = new Blob([res.data], { type: 'application/pdf' });
                                                            const objectUrl = URL.createObjectURL(blob);
                                                            const a = document.createElement('a');
                                                            a.href = objectUrl;
                                                            a.download = `pointage-${safeMois}.pdf`;
                                                            document.body.appendChild(a);
                                                            a.click();
                                                            document.body.removeChild(a);
                                                            setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
                                                        } catch (err) {
                                                            toast.error(err?.response?.data?.detail || 'Erreur lors du téléchargement du PDF');
                                                        }
                                                    }}
                                                    className="p-1.5 rounded text-gray-500 hover:text-[#1B365D] hover:bg-gray-100 transition-colors"
                                                    title={`Télécharger le PDF de ${m.mois_label}`}
                                                    data-testid={`mens-pdf-${m.id}`}
                                                >
                                                    <Download className="w-3.5 h-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => deleteMensualite(m)}
                                                    className="p-1.5 rounded text-red-500 hover:text-white hover:bg-red-500"
                                                    title="Supprimer"
                                                    data-testid={`mens-delete-${m.id}`}
                                                >
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </div>
                                        {isOpen && (
                                            <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 space-y-3">
                                                {/* Récap chiffres */}
                                                <div className="text-xs grid grid-cols-2 md:grid-cols-4 gap-3">
                                                    <Detail label="Base" value={fmtEur(m.base)} />
                                                    <Detail label="Jours" value={`${m.nb_jours} × ${fmtEur(m.tarif)}`} />
                                                    <Detail label="Paniers" value={`${m.nb_paniers} → ${fmtEur(m.total_paniers)}`} />
                                                    <Detail label="Autres" value={fmtEur(m.total_autres)} />
                                                </div>

                                                {/* Listes Dépenses / Paiements côte à côte */}
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <LinesPanel
                                                        title="Dépenses (+)"
                                                        accent="red"
                                                        lines={m.depenses}
                                                        total={m.total_depenses}
                                                        onAdd={() => openLineDialog(m.id, 'depenses')}
                                                        onDelete={(lineId) => deleteLine(m.id, 'depenses', lineId)}
                                                        testKey={`dep-${m.id}`}
                                                    />
                                                    <LinesPanel
                                                        title="Paiements (−)"
                                                        accent="emerald"
                                                        lines={m.paiements}
                                                        total={m.total_paiements}
                                                        onAdd={() => openLineDialog(m.id, 'paiements')}
                                                        onDelete={(lineId) => deleteLine(m.id, 'paiements', lineId)}
                                                        testKey={`pai-${m.id}`}
                                                    />
                                                </div>

                                                {/* Résumé final */}
                                                <div className="flex items-center justify-end gap-4 text-xs pt-2 border-t border-gray-200">
                                                    <span><span className="text-gray-500">Base : </span><b>{fmtEur(m.base)}</b></span>
                                                    <span className="text-red-600">+{fmtEur(m.total_depenses)}</span>
                                                    <span className="text-emerald-600">−{fmtEur(m.total_paiements)}</span>
                                                    <span className="text-blue-700 font-bold">= VF {fmtEur(m.vente_finale)}</span>
                                                </div>
                                                {m.remarque && (
                                                    <div className="text-[11px] text-gray-500 italic">Remarque : {m.remarque}</div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Dialog — édition infos */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent className="max-w-md" data-testid="fiche-edit-dialog">
                    <DialogHeader><DialogTitle className="text-[#1B365D]">Modifier les informations</DialogTitle></DialogHeader>
                    <div className="space-y-3 pt-1">
                        <LabeledInput label="Nom *" value={editForm.nom} onChange={v => setEditForm(p => ({ ...p, nom: v }))} testId="edit-nom" />
                        <LabeledInput label="Prénom" value={editForm.prenom} onChange={v => setEditForm(p => ({ ...p, prenom: v }))} testId="edit-prenom" />
                        <div className="grid grid-cols-2 gap-2">
                            <LabeledInput label="Téléphone" value={editForm.telephone} onChange={v => setEditForm(p => ({ ...p, telephone: v }))} testId="edit-tel" />
                            <LabeledInput label="E-mail" type="email" value={editForm.email} onChange={v => setEditForm(p => ({ ...p, email: v }))} testId="edit-email" />
                        </div>
                        <LabeledInput label="Tarif / jour (€)" type="number" step="0.01" value={editForm.tarif_journee} onChange={v => setEditForm(p => ({ ...p, tarif_journee: v }))} testId="edit-tarif" />
                    </div>
                    <DialogFooter>
                        <button onClick={() => setEditOpen(false)} className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100">Annuler</button>
                        <button onClick={saveEdit} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-[#1B365D] text-white text-sm font-semibold hover:bg-[#152a4a]" data-testid="edit-save">
                            <Save className="w-4 h-4" /> Enregistrer
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog — création mensualité */}
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
                <DialogContent className="max-w-md" data-testid="mens-create-dialog">
                    <DialogHeader><DialogTitle className="text-[#1B365D]">Créer un calcul mensuel</DialogTitle></DialogHeader>
                    <div className="space-y-3 pt-1">
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">Mois *</label>
                            <select
                                value={createForm.mois}
                                onChange={e => setCreateForm(p => ({ ...p, mois: e.target.value }))}
                                className="w-full h-9 px-3 rounded-md border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1B365D]/30"
                                data-testid="mens-mois"
                            >
                                {monthOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <p className="text-[11px] text-gray-400 mt-1">La base sera calculée automatiquement à partir des journées du mois. Les dépenses et paiements seront ajoutés ligne par ligne.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setCreateOpen(false)} className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100">Annuler</button>
                        <button onClick={saveCreate} className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700" data-testid="mens-create-save">
                            <Plus className="w-4 h-4" /> Créer
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialog — ajout ligne dépense ou paiement */}
            <Dialog open={lineDialog.open} onOpenChange={(o) => setLineDialog(p => ({ ...p, open: o }))}>
                <DialogContent className="max-w-md" data-testid="line-dialog">
                    <DialogHeader>
                        <DialogTitle className={lineDialog.kind === 'depenses' ? 'text-red-700' : 'text-emerald-700'}>
                            {lineDialog.kind === 'depenses' ? 'Ajouter une dépense' : 'Ajouter un paiement'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3 pt-1">
                        <div className="grid grid-cols-2 gap-2">
                            <LabeledInput label="Montant (€) *" type="number" step="0.01" value={lineForm.montant} onChange={v => setLineForm(p => ({ ...p, montant: v }))} testId="line-montant" />
                            <LabeledInput label="Date" type="date" value={lineForm.date} onChange={v => setLineForm(p => ({ ...p, date: v }))} testId="line-date" />
                        </div>
                        <LabeledInput label="Remarque" value={lineForm.remarque} onChange={v => setLineForm(p => ({ ...p, remarque: v }))} testId="line-remarque" />
                    </div>
                    <DialogFooter>
                        <button onClick={() => setLineDialog({ open: false, kind: null, mid: null })} className="px-3 py-1.5 rounded-md text-sm text-gray-600 hover:bg-gray-100">Annuler</button>
                        <button
                            onClick={saveLine}
                            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-white text-sm font-semibold ${lineDialog.kind === 'depenses' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                            data-testid="line-save"
                        >
                            <Plus className="w-4 h-4" /> Ajouter
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}

function InfoCard({ icon: Icon, color, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
                <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</div>
                <div className="text-sm font-bold text-[#1B365D] truncate" title={value}>{value}</div>
            </div>
        </div>
    );
}

function Detail({ label, value, color }) {
    return (
        <div>
            <div className="text-[9px] uppercase tracking-wider text-gray-400">{label}</div>
            <div className={`text-sm ${color || 'text-gray-700'}`}>{value}</div>
        </div>
    );
}

function LabeledInput({ label, value, onChange, type = 'text', step, testId }) {
    return (
        <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
            <Input type={type} step={step} value={value} onChange={e => onChange(e.target.value)} className="h-9 text-sm" data-testid={testId} />
        </div>
    );
}

function LinesPanel({ title, accent, lines, total, onAdd, onDelete, testKey }) {
    const isRed = accent === 'red';
    const colors = isRed
        ? { ring: 'border-red-100', bg: 'bg-red-50/40', total: 'text-red-600', addBg: 'bg-red-100 hover:bg-red-200 text-red-700' }
        : { ring: 'border-emerald-100', bg: 'bg-emerald-50/40', total: 'text-emerald-600', addBg: 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700' };
    const arr = lines || [];
    return (
        <div className={`rounded-lg border ${colors.ring} ${colors.bg} p-2`} data-testid={`lines-panel-${testKey}`}>
            <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-wider text-gray-600 font-bold">{title}</div>
                <button onClick={onAdd} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${colors.addBg}`} data-testid={`add-line-${testKey}`}>
                    <Plus className="w-3 h-3" /> Ajouter
                </button>
            </div>
            {arr.length === 0 ? (
                <div className="text-[11px] text-gray-400 italic px-2 py-3 text-center">Aucune ligne.</div>
            ) : (
                <div className="space-y-1">
                    {arr.map(l => (
                        <div key={l.id} className="flex items-center gap-2 bg-white border border-gray-100 rounded px-2 py-1.5 text-xs" data-testid={`line-row-${l.id}`}>
                            <div className={`font-bold ${colors.total} min-w-[60px]`}>
                                {Number(l.montant || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                            </div>
                            <div className="text-gray-500 text-[10px] whitespace-nowrap">{l.date || '—'}</div>
                            <div className="flex-1 min-w-0 truncate text-gray-600 text-[11px]" title={l.remarque}>
                                {l.remarque || <span className="text-gray-300 italic">—</span>}
                            </div>
                            <button
                                onClick={() => onDelete(l.id)}
                                className="text-gray-400 hover:text-red-500 p-0.5"
                                title="Supprimer"
                                data-testid={`delete-line-${l.id}`}
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <div className="flex items-center justify-between mt-2 pt-1.5 border-t border-gray-100 text-[11px]">
                <span className="text-gray-500 font-semibold">Total</span>
                <span className={`font-bold ${colors.total}`}>
                    {Number(total || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </span>
            </div>
        </div>
    );
}
